import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import { Input } from '@/components/ui/input'
import { Search, Loader2, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Person } from '@/types'

interface PersonMatchListProps {
  groupId: string
  excludePersonId?: string | null
  onSelect: (person: Person) => void
  disabled?: boolean
}

/**
 * Touch-friendly list of persons for the "match to existing" action.
 * Cards are full-width with large tap targets — no dropdowns.
 */
export function PersonMatchList({
  groupId,
  excludePersonId,
  onSelect,
  disabled,
}: PersonMatchListProps) {
  const { t } = useTranslation()
  const [persons, setPersons] = useState<Person[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchPersons = async (search?: string) => {
    if (!groupId) return
    if (search) setSearching(true)
    else setLoading(true)
    try {
      const fn = httpsCallable(functions, 'getPersons')
      const params: Record<string, unknown> = { groupId, pageSize: 30 }
      if (search) params.search = search
      const res = await fn(params)
      const data = res.data as { success: boolean; persons: Person[] }
      if (data.success) setPersons(data.persons)
    } catch (err) {
      console.error('Failed to load persons:', err)
    } finally {
      setSearching(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPersons()
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [groupId])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchPersons(query.trim() || undefined)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  const visible = useMemo(
    () => persons.filter((p) => p.id !== excludePersonId),
    [persons, excludePersonId]
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('feed.drawer.searchPerson')}
          className="h-11 pl-9"
          disabled={disabled}
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ink-soft" />
        )}
      </div>

      <div className="flex max-h-[55vh] flex-col gap-2 overflow-y-auto sm:max-h-[60vh]">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-ink-soft" />
          </div>
        ) : visible.length === 0 ? (
          <p className="py-6 text-center text-[14px] text-ink-soft">
            {t('feed.drawer.noPersons')}
          </p>
        ) : (
          visible.map((person) => (
            <button
              key={person.id}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(person)}
              className={cn(
                'flex items-center gap-3 rounded-lg border border-rule bg-paper-2/40 p-3 text-left transition',
                'hover:border-gold-soft hover:bg-paper-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-paper-3">
                {person.thumbnailUrl ? (
                  <img
                    src={person.thumbnailUrl}
                    alt={person.name || ''}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-ink-soft">
                    <User className="h-5 w-5" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-sans text-[15px] font-medium text-ink">
                  {person.name || t('common.unknown')}
                </div>
                <div className="flex items-center gap-1.5">
                  {person.classificationColor && (
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: person.classificationColor }}
                    />
                  )}
                  <span className="text-[12px] text-ink-soft">
                    {person.classificationName || ''}
                    {person.totalSightings > 0 && (
                      <>
                        {' · '}
                        {t('persons.sightings', { count: person.totalSightings })}
                      </>
                    )}
                  </span>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  )
}
